import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Event as EventIcon,
  ViewModule as CalendarViewIcon,
  List as ListIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import ORScheduleCalendar from './ORScheduleCalendar';

const SurgicalSchedule = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSurgery, setSelectedSurgery] = useState(null);
  const [dialogType, setDialogType] = useState('add');
  const [activeTab, setActiveTab] = useState(0);

  const [surgeries, setSurgeries] = useState([
    {
      id: 1,
      patientName: 'John Smith',
      surgeryType: 'Cardiac Bypass',
      scheduledDate: '2024-01-16',
      scheduledTime: '08:00',
      estimatedDuration: '4 hours',
      room: 'OR-1',
      team: 'Cardiac Team A',
      surgeon: 'Dr. Sarah Wilson',
      status: 'scheduled',
      priority: 'high',
      notes: 'Patient has history of heart disease'
    },
    {
      id: 2,
      patientName: 'Sarah Johnson',
      surgeryType: 'Appendectomy',
      scheduledDate: '2024-01-16',
      scheduledTime: '10:30',
      estimatedDuration: '1.5 hours',
      room: 'OR-3',
      team: 'General Surgery Team',
      surgeon: 'Dr. Amanda Davis',
      status: 'in-progress',
      priority: 'medium',
      notes: 'Emergency case'
    },
    {
      id: 3,
      patientName: 'Michael Brown',
      surgeryType: 'Hip Replacement',
      scheduledDate: '2024-01-16',
      scheduledTime: '14:00',
      estimatedDuration: '3 hours',
      room: 'OR-2',
      team: 'Orthopedic Team',
      surgeon: 'Dr. James Brown',
      status: 'scheduled',
      priority: 'medium',
      notes: 'Elective surgery'
    },
    {
      id: 4,
      patientName: 'Emily Davis',
      surgeryType: 'Cataract Surgery',
      scheduledDate: '2024-01-17',
      scheduledTime: '09:00',
      estimatedDuration: '1 hour',
      room: 'OR-5',
      team: 'Ophthalmology Team',
      surgeon: 'Dr. Lisa Garcia',
      status: 'scheduled',
      priority: 'low',
      notes: 'Outpatient procedure'
    },
    {
      id: 5,
      patientName: 'Robert Wilson',
      surgeryType: 'Brain Tumor Removal',
      scheduledDate: '2024-01-17',
      scheduledTime: '07:00',
      estimatedDuration: '6 hours',
      room: 'OR-4',
      team: 'Neurosurgery Team',
      surgeon: 'Dr. David Chen',
      status: 'scheduled',
      priority: 'high',
      notes: 'Complex case, requires special equipment'
    }
  ]);

  const [formData, setFormData] = useState({
    patientName: '',
    surgeryType: '',
    scheduledDate: '',
    scheduledTime: '',
    estimatedDuration: '',
    room: '',
    team: '',
    surgeon: '',
    priority: '',
    notes: ''
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return '#2196f3';
      case 'in-progress': return '#ff9800';
      case 'completed': return '#4caf50';
      case 'cancelled': return '#f44336';
      case 'postponed': return '#9c27b0';
      default: return '#757575';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled': return <ScheduleIcon />;
      case 'in-progress': return <WarningIcon />;
      case 'completed': return <CheckCircleIcon />;
      case 'cancelled': return <ErrorIcon />;
      case 'postponed': return <CalendarIcon />;
      default: return <ErrorIcon />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#f44336';
      case 'medium': return '#ff9800';
      case 'low': return '#4caf50';
      default: return '#757575';
    }
  };

  const handleOpenDialog = (type, surgery = null) => {
    setDialogType(type);
    setSelectedSurgery(surgery);
    if (type === 'add') {
      setFormData({
        patientName: '',
        surgeryType: '',
        scheduledDate: '',
        scheduledTime: '',
        estimatedDuration: '',
        room: '',
        team: '',
        surgeon: '',
        priority: '',
        notes: ''
      });
    } else if (surgery) {
      setFormData({
        patientName: surgery.patientName,
        surgeryType: surgery.surgeryType,
        scheduledDate: surgery.scheduledDate,
        scheduledTime: surgery.scheduledTime,
        estimatedDuration: surgery.estimatedDuration,
        room: surgery.room,
        team: surgery.team,
        surgeon: surgery.surgeon,
        priority: surgery.priority,
        notes: surgery.notes
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSurgery(null);
  };

  const handleSubmit = () => {
    if (dialogType === 'add') {
      const newSurgery = {
        id: surgeries.length + 1,
        ...formData,
        status: 'scheduled'
      };
      setSurgeries([...surgeries, newSurgery]);
    } else if (dialogType === 'edit' && selectedSurgery) {
      const updatedSurgeries = surgeries.map(surgery =>
        surgery.id === selectedSurgery.id ? { ...surgery, ...formData } : surgery
      );
      setSurgeries(updatedSurgeries);
    }
    handleCloseDialog();
  };

  const handleDeleteSurgery = (surgeryId) => {
    setSurgeries(surgeries.filter(surgery => surgery.id !== surgeryId));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const SurgeryCard = ({ surgery }) => (
    <Card sx={{ 
      height: '100%',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: getStatusColor(surgery.status) }}>
              {getStatusIcon(surgery.status)}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {surgery.patientName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {surgery.surgeryType}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Chip
              label={surgery.status}
              size="small"
              sx={{
                bgcolor: getStatusColor(surgery.status),
                color: 'white',
                fontWeight: 500,
                textTransform: 'capitalize'
              }}
            />
            <Chip
              label={surgery.priority}
              size="small"
              variant="outlined"
              sx={{
                borderColor: getPriorityColor(surgery.priority),
                color: getPriorityColor(surgery.priority),
                fontWeight: 500,
                textTransform: 'capitalize'
              }}
            />
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Schedule
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="caption">
              {surgery.scheduledDate} at {surgery.scheduledTime}
            </Typography>
            <Typography variant="caption">
              {surgery.estimatedDuration}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Team & Location
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography variant="caption">
              {surgery.room}
            </Typography>
            <Typography variant="caption">
              {surgery.team}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {surgery.surgeon}
          </Typography>
        </Box>

        {surgery.notes && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Notes
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {surgery.notes}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="View Details">
              <IconButton size="small" onClick={() => handleOpenDialog('view', surgery)}>
                <ViewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Surgery">
              <IconButton size="small" onClick={() => handleOpenDialog('edit', surgery)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Surgery">
              <IconButton size="small" onClick={() => handleDeleteSurgery(surgery.id)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EventIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {surgery.scheduledDate}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
            Surgical Schedule
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage surgical appointments and scheduling
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('add')}
          sx={{ borderRadius: 2 }}
        >
          Schedule Surgery
        </Button>
      </Box>

      {/* View Toggle Tabs */}
      <Paper elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              minHeight: 56,
              fontSize: '0.9rem',
              fontWeight: 500,
              textTransform: 'none',
              color: '#6c757d',
              '&.Mui-selected': {
                color: '#708bff',
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#708bff',
              height: 3,
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarViewIcon />
                Calendar View
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ListIcon />
                List View
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box sx={{ minHeight: '600px' }}>
          <ORScheduleCalendar />
        </Box>
      )}

      {activeTab === 1 && (
        <Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#2196f3', mb: 1 }}>
              {surgeries.filter(s => s.status === 'scheduled').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Scheduled
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#ff9800', mb: 1 }}>
              {surgeries.filter(s => s.status === 'in-progress').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              In Progress
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#4caf50', mb: 1 }}>
              {surgeries.filter(s => s.status === 'completed').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Completed
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#9c27b0', mb: 1 }}>
              {surgeries.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Surgeries
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Surgery Cards */}
      <Grid container spacing={3}>
        {surgeries.map((surgery) => (
          <Grid item xs={12} sm={6} md={4} key={surgery.id}>
            <SurgeryCard surgery={surgery} />
          </Grid>
        ))}
      </Grid>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon />
            {dialogType === 'add' ? 'Schedule Surgery' : 
             dialogType === 'edit' ? 'Edit Surgery' : 'Surgery Details'}
          </Box>
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {dialogType === 'view' && selectedSurgery ? (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Patient Information</Typography>
                  <List>
                    <ListItem>
                      <ListItemText primary="Patient Name" secondary={selectedSurgery.patientName} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Surgery Type" secondary={selectedSurgery.surgeryType} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Status" secondary={selectedSurgery.status} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Priority" secondary={selectedSurgery.priority} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Schedule & Team</Typography>
                  <List>
                    <ListItem>
                      <ListItemText primary="Date" secondary={selectedSurgery.scheduledDate} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Time" secondary={selectedSurgery.scheduledTime} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Duration" secondary={selectedSurgery.estimatedDuration} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Room" secondary={selectedSurgery.room} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Team" secondary={selectedSurgery.team} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Surgeon" secondary={selectedSurgery.surgeon} />
                    </ListItem>
                  </List>
                </Grid>
                {selectedSurgery.notes && (
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Notes</Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="body2">{selectedSurgery.notes}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Patient Name"
                  value={formData.patientName}
                  onChange={(e) => setFormData({ ...formData, patientName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Surgery Type"
                  value={formData.surgeryType}
                  onChange={(e) => setFormData({ ...formData, surgeryType: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Scheduled Date"
                  value={formData.scheduledDate ? new Date(formData.scheduledDate) : null}
                  onChange={(date) => setFormData({ ...formData, scheduledDate: date ? date.toISOString().split('T')[0] : '' })}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Scheduled Time"
                  type="time"
                  value={formData.scheduledTime}
                  onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Estimated Duration"
                  value={formData.estimatedDuration}
                  onChange={(e) => setFormData({ ...formData, estimatedDuration: e.target.value })}
                  placeholder="e.g., 2 hours"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Operating Room</InputLabel>
                  <Select
                    value={formData.room}
                    label="Operating Room"
                    onChange={(e) => setFormData({ ...formData, room: e.target.value })}
                  >
                    <MenuItem value="OR-1">OR-1</MenuItem>
                    <MenuItem value="OR-2">OR-2</MenuItem>
                    <MenuItem value="OR-3">OR-3</MenuItem>
                    <MenuItem value="OR-4">OR-4</MenuItem>
                    <MenuItem value="OR-5">OR-5</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Surgical Team"
                  value={formData.team}
                  onChange={(e) => setFormData({ ...formData, team: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Surgeon"
                  value={formData.surgeon}
                  onChange={(e) => setFormData({ ...formData, surgeon: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    label="Priority"
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  >
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        {dialogType !== 'view' && (
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {dialogType === 'add' ? 'Schedule Surgery' : 'Save Changes'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
        </Box>
      )}
    </Box>
  );
};

export default SurgicalSchedule; 